import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { BessHoraResultado, BessParametros } from "../../types";

export const HOURS = Array.from({ length: 24 }, (_, i) => i + 1);

const SERIES_COLORS = {
  cargaRede: "#0072ce",
  cargaSolar: "#c77f00",
  descargaConsumo: "#1fa824",
  descargaInjecao: "#0e9aa7",
  consumo: "#212322",
  consumoOriginal: "#7e7e82",
  geracao: "#c77f00",
  soc: "#6f42c1",
  pld: "#7e7e82",
};

const MCP_NEGATIVE_COLOR = "#f91010";
const MCP_POSITIVE_COLOR = "#1fa824";

export function listDays(horaria: BessHoraResultado[]): string[] {
  const days: string[] = [];
  for (const row of horaria) {
    if (days[days.length - 1] !== row.data) days.push(row.data);
  }
  return days;
}

export function groupByDay(
  horaria: BessHoraResultado[]
): Record<string, BessHoraResultado[]> {
  return horaria.reduce<Record<string, BessHoraResultado[]>>((acc, row) => {
    (acc[row.data] = acc[row.data] ?? []).push(row);
    return acc;
  }, {});
}

/** Primeiro dia em que a bateria opera; fallback: primeiro dia da medição. */
export function getDefaultDay(horaria: BessHoraResultado[]): string {
  const active = horaria.find((row) => row.acao !== "ocioso");
  return active?.data ?? horaria[0]?.data ?? "";
}

export function isWeekendDate(data: string): boolean {
  const [year, month, day] = data.split("-").map(Number);
  const weekday = new Date(year, month - 1, day).getDay();
  return weekday === 0 || weekday === 6;
}

export function formatDayLabel(data: string): string {
  const [year, month, day] = data.split("-");
  return `${day}/${month}/${year}`;
}

type HourlyValue = number | null;

export interface DaySeries {
  cargaRede: HourlyValue[];
  cargaSolar: HourlyValue[];
  descargaConsumo: HourlyValue[];
  descargaInjecao: HourlyValue[];
  consumoOriginal: HourlyValue[];
  consumoFinal: HourlyValue[];
  geracaoOriginal: HourlyValue[];
  geracaoAjustada: HourlyValue[];
  soc: HourlyValue[];
  pld: HourlyValue[];
  mcpDelta: HourlyValue[];
  rows: Array<BessHoraResultado | null>;
}

/** Converte as linhas do dia em séries indexadas pela hora 1–24 (null = hora ausente). */
export function buildDaySeries(rows: BessHoraResultado[]): DaySeries {
  const byHour = new Map(rows.map((row) => [row.hora, row]));
  const pick = (getter: (row: BessHoraResultado) => number): HourlyValue[] =>
    HOURS.map((hour) => {
      const row = byHour.get(hour);
      return row ? getter(row) : null;
    });

  return {
    cargaRede: pick((r) => r.energiaCargaRedeAc),
    cargaSolar: pick((r) => r.energiaCargaSolarAc),
    descargaConsumo: pick((r) => r.energiaDescargaConsumoAc),
    descargaInjecao: pick((r) => r.energiaInjetadaKwh),
    consumoOriginal: pick((r) => r.consumoOriginal),
    consumoFinal: pick((r) => r.consumoFinal),
    geracaoOriginal: pick((r) => r.geracaoOriginal),
    geracaoAjustada: pick((r) => r.geracaoAjustada),
    soc: pick((r) => r.soc),
    pld: pick((r) => r.pld),
    mcpDelta: pick((r) => r.mcpDelta),
    rows: HOURS.map((hour) => byHour.get(hour) ?? null),
  };
}

/** Faixa da janela de ponta no eixo de categorias (hora h → índice h-1). */
export function buildPeakBand(
  horasPonta: number[]
): { from: number; to: number } | null {
  if (horasPonta.length === 0) return null;
  const sorted = [...horasPonta].sort((a, b) => a - b);
  return { from: sorted[0] - 1.5, to: sorted[sorted.length - 1] - 0.5 };
}

export interface AxisRange {
  min: number;
  max: number;
}

export interface BessAxisRanges {
  kwh: AxisRange;
  soc: AxisRange;
  pld: AxisRange;
  mcp: AxisRange;
  showCapacityLine: boolean;
}

/** Passo "redondo" (1/2/5 × 10ⁿ) para ~10 divisões num intervalo. */
function niceStep(span: number): number {
  const magnitude = Math.pow(10, Math.floor(Math.log10(span)));
  const normalized = span / magnitude;
  const factor =
    normalized <= 1 ? 0.1 : normalized <= 2 ? 0.2 : normalized <= 5 ? 0.5 : 1;
  return factor * magnitude;
}

/** Arredonda o intervalo para fora, até múltiplos de um passo redondo. */
export function niceRange(min: number, max: number): AxisRange {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
  if (max === min) {
    const pad = Math.abs(max) || 1;
    return { min: min - pad, max: max + pad };
  }
  const step = niceStep(max - min);
  return {
    min: Math.floor(min / step) * step,
    max: Math.ceil(max / step) * step,
  };
}

/**
 * Escalas fixas para os quatro painéis, calculadas sobre o mês inteiro: os
 * eixos não mudam ao navegar entre dias, então dias diferentes ficam
 * visualmente comparáveis.
 */
export function buildAxisRanges(
  horaria: BessHoraResultado[],
  capacidadeUtilKwh: number
): BessAxisRanges {
  const kwhValues: number[] = [0];
  const socValues: number[] = [0];
  const pldValues: number[] = [];
  const mcpValues: number[] = [0];

  for (const row of horaria) {
    kwhValues.push(
      row.energiaCargaRedeAc + row.energiaCargaSolarAc,
      row.energiaDescargaConsumoAc + row.energiaInjetadaKwh,
      row.consumoOriginal,
      row.consumoFinal,
      row.geracaoOriginal,
      row.geracaoAjustada
    );
    socValues.push(row.soc);
    pldValues.push(row.pld);
    mcpValues.push(row.mcpDelta);
  }

  const socMax = Math.max(...socValues);
  const showCapacityLine = socMax >= capacidadeUtilKwh * 0.5;

  return {
    kwh: niceRange(Math.min(...kwhValues), Math.max(...kwhValues)),
    soc: showCapacityLine
      ? { min: 0, max: capacidadeUtilKwh }
      : niceRange(0, socMax),
    pld: pldValues.length
      ? niceRange(Math.min(...pldValues), Math.max(...pldValues))
      : { min: 0, max: 1 },
    mcp: niceRange(Math.min(...mcpValues), Math.max(...mcpValues)),
    showCapacityLine,
  };
}

const CATEGORIES = HOURS.map((hour) => `${hour}h`);

const basePanelOptions = {
  chart: {
    backgroundColor: "#FAFAFA",
    borderRadius: 8,
    spacingBottom: 8,
  },
  title: { text: "" },
  credits: { enabled: false },
  xAxis: { categories: CATEGORIES },
};

const ACAO_LABELS: Record<BessHoraResultado["acao"], string> = {
  carregar: "carregar",
  descarregar: "descarregar",
  ocioso: "ocioso",
};

function hourTooltipHeader(row: BessHoraResultado | null, hour: number) {
  if (!row) return `${hour}h — sem medição`;
  const fonte = row.fonteCarga ? ` (${row.fonteCarga})` : "";
  return `${hour}h — ${ACAO_LABELS[row.acao]}${fonte}`;
}

interface BessDayChartProps {
  horaria: BessHoraResultado[];
  parametros: BessParametros;
  selectedDay: string;
  onSelectDay: (data: string) => void;
}

export const BessDayChart = ({
  horaria,
  parametros,
  selectedDay,
  onSelectDay,
}: BessDayChartProps) => {
  const days = listDays(horaria);
  const rows = groupByDay(horaria)[selectedDay] ?? [];
  const series = buildDaySeries(rows);
  const ranges = React.useMemo(
    () => buildAxisRanges(horaria, parametros.capacidadeUtilKwh),
    [horaria, parametros.capacidadeUtilKwh]
  );
  // Fim de semana não tem horário de ponta — sem faixa no gráfico.
  const peakBand = isWeekendDate(selectedDay)
    ? null
    : buildPeakBand(parametros.horasPonta);
  const dayIndex = days.indexOf(selectedDay);

  const kwhTooltipFormatter = function (
    this: Highcharts.TooltipFormatterContextObject
  ): string {
    const hourIndex = (this.points?.[0]?.point.index ??
      this.point.index) as number;
    const row = series.rows[hourIndex];
    const lines = [
      `<b>${hourTooltipHeader(row, hourIndex + 1)}</b>`,
      ...(this.points ?? [this]).map(
        (p) =>
          `${p.series.name}: <b>${(p.y ?? 0).toLocaleString("pt-BR", {
            maximumFractionDigits: 0,
          })} kWh</b>`
      ),
    ];
    if (row) {
      lines.push(
        `SoC: <b>${row.soc.toLocaleString("pt-BR", {
          maximumFractionDigits: 0,
        })} kWh</b>`,
        `PLD: <b>R$ ${row.pld.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}/MWh</b>`,
        `MCP: <b>${row.mcpDelta.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })}</b>`
      );
    }
    return lines.join("<br/>");
  };

  const kwhOptions: Highcharts.Options = {
    ...basePanelOptions,
    chart: { ...basePanelOptions.chart, height: 320 },
    xAxis: {
      categories: CATEGORIES,
      plotBands: peakBand
        ? [
            {
              from: peakBand.from,
              to: peakBand.to,
              color: "rgba(33, 35, 34, 0.06)",
              label: { text: "ponta", style: { color: "#7e7e82" } },
            },
          ]
        : [],
    },
    yAxis: {
      title: { text: "kWh" },
      gridLineWidth: 0,
      min: ranges.kwh.min,
      max: ranges.kwh.max,
      startOnTick: false,
      endOnTick: false,
    },
    tooltip: { shared: true, formatter: kwhTooltipFormatter },
    plotOptions: {
      column: { borderRadius: 2, borderWidth: 0 },
      series: { marker: { enabled: false } },
    },
    series: [
      {
        type: "column",
        name: "Carga (rede)",
        data: series.cargaRede,
        color: SERIES_COLORS.cargaRede,
        stack: "carga",
        stacking: "normal",
      },
      {
        type: "column",
        name: "Carga (solar)",
        data: series.cargaSolar,
        color: SERIES_COLORS.cargaSolar,
        stack: "carga",
        stacking: "normal",
      },
      {
        type: "column",
        name: "Descarga (consumo)",
        data: series.descargaConsumo,
        color: SERIES_COLORS.descargaConsumo,
        stack: "descarga",
        stacking: "normal",
      },
      {
        type: "column",
        name: "Descarga (injeção)",
        data: series.descargaInjecao,
        color: SERIES_COLORS.descargaInjecao,
        stack: "descarga",
        stacking: "normal",
      },
      {
        type: "line",
        name: "Consumo original",
        data: series.consumoOriginal,
        color: SERIES_COLORS.consumoOriginal,
        dashStyle: "Dash",
      },
      {
        type: "line",
        name: "Consumo final",
        data: series.consumoFinal,
        color: SERIES_COLORS.consumo,
      },
      {
        type: "line",
        name: "Geração original",
        data: series.geracaoOriginal,
        color: SERIES_COLORS.geracao,
        dashStyle: "Dash",
        opacity: 0.85,
      },
      {
        type: "line",
        name: "Geração final",
        data: series.geracaoAjustada,
        color: SERIES_COLORS.geracao,
      },
    ],
  };

  // A escala vai até a capacidade útil só quando o SoC do mês chega perto dela;
  // caso contrário fecha nos dados para as variações ficarem visíveis.
  const showCapacityLine = ranges.showCapacityLine;

  const socOptions: Highcharts.Options = {
    ...basePanelOptions,
    chart: { ...basePanelOptions.chart, height: 150 },
    yAxis: {
      title: { text: "SoC (kWh)" },
      gridLineWidth: 0,
      min: ranges.soc.min,
      max: ranges.soc.max,
      startOnTick: false,
      endOnTick: false,
      plotLines: showCapacityLine
        ? [
            {
              value: parametros.capacidadeUtilKwh,
              color: SERIES_COLORS.soc,
              dashStyle: "Dash",
              width: 1,
              label: {
                text: "capacidade útil",
                style: { color: SERIES_COLORS.soc, fontSize: "10px" },
              },
            },
          ]
        : [],
    },
    legend: { enabled: false },
    tooltip: {
      formatter: function (
        this: Highcharts.TooltipFormatterContextObject
      ): string {
        return `<b>${this.x}</b><br/>SoC: <b>${(
          this.y ?? 0
        ).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kWh</b>`;
      },
    },
    series: [
      {
        type: "area",
        name: "SoC",
        data: series.soc,
        color: SERIES_COLORS.soc,
        fillOpacity: 0.12,
        marker: { enabled: false },
      },
    ],
  };

  const pldOptions: Highcharts.Options = {
    ...basePanelOptions,
    chart: { ...basePanelOptions.chart, height: 130 },
    // startOnTick/endOnTick desligados: o eixo abraça o intervalo real do PLD
    // em vez de arredondar para ticks largos que achatam a curva.
    yAxis: {
      title: { text: "R$/MWh" },
      gridLineWidth: 0,
      min: ranges.pld.min,
      max: ranges.pld.max,
      startOnTick: false,
      endOnTick: false,
    },
    legend: { enabled: false },
    tooltip: {
      formatter: function (
        this: Highcharts.TooltipFormatterContextObject
      ): string {
        return `<b>${this.x}</b><br/>PLD: <b>R$ ${(this.y ?? 0).toLocaleString(
          "pt-BR",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        )}/MWh</b>`;
      },
    },
    series: [
      {
        type: "line",
        name: "PLD",
        data: series.pld,
        color: SERIES_COLORS.pld,
        marker: { enabled: false },
      },
    ],
  };

  const mcpOptions: Highcharts.Options = {
    ...basePanelOptions,
    chart: { ...basePanelOptions.chart, height: 150 },
    yAxis: [
      {
        title: { text: "MCP (R$)" },
        gridLineWidth: 0,
        min: ranges.mcp.min,
        max: ranges.mcp.max,
        startOnTick: false,
        endOnTick: false,
      },
      {
        title: { text: "PLD (R$/MWh)" },
        gridLineWidth: 0,
        opposite: true,
        min: ranges.pld.min,
        max: ranges.pld.max,
        startOnTick: false,
        endOnTick: false,
      },
    ],
    tooltip: {
      shared: true,
      formatter: function (
        this: Highcharts.TooltipFormatterContextObject
      ): string {
        const lines = (this.points ?? [this]).map((p) => {
          if (p.series.name === "PLD") {
            return `PLD: <b>R$ ${(p.y ?? 0).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}/MWh</b>`;
          }
          return `MCP: <b>${(p.y ?? 0).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })}</b>`;
        });
        return `<b>${this.x}</b><br/>${lines.join("<br/>")}`;
      },
    },
    plotOptions: {
      column: { borderRadius: 2, borderWidth: 0 },
      series: { marker: { enabled: false } },
    },
    series: [
      {
        type: "column",
        name: "MCP",
        yAxis: 0,
        data: series.mcpDelta,
        zones: [
          { value: 0, color: MCP_NEGATIVE_COLOR },
          { color: MCP_POSITIVE_COLOR },
        ],
      },
      {
        type: "line",
        name: "PLD",
        yAxis: 1,
        data: series.pld,
        color: SERIES_COLORS.pld,
        dashStyle: "Dash",
        opacity: 0.7,
      },
    ],
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          margin: "32px 0 6px",
        }}
      >
        <h4 style={{ margin: 0, fontWeight: 500 }}>Perfil horário do dia</h4>
        <button
          type="button"
          aria-label="Dia anterior"
          disabled={dayIndex <= 0}
          onClick={() => onSelectDay(days[dayIndex - 1])}
        >
          ‹
        </button>
        <select
          aria-label="Selecionar dia"
          value={selectedDay}
          onChange={(event) => onSelectDay(event.target.value)}
        >
          {days.map((day) => (
            <option key={day} value={day}>
              {formatDayLabel(day)}
              {isWeekendDate(day) ? " — fim de semana" : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          aria-label="Próximo dia"
          disabled={dayIndex < 0 || dayIndex >= days.length - 1}
          onClick={() => onSelectDay(days[dayIndex + 1])}
        >
          ›
        </button>
      </div>

      <p style={{ color: "#7e7e82", fontSize: 12, margin: "0 0 8px" }}>
        kWh por hora · faixa cinza = janela de ponta · clique na legenda para
        ligar/desligar categorias
      </p>
      <HighchartsReact highcharts={Highcharts} options={kwhOptions} />

      <p style={{ color: "#7e7e82", fontSize: 12, margin: "20px 0 8px" }}>
        Estado de carga (SoC)
        {showCapacityLine ? " · linha pontilhada = capacidade útil" : ""}
      </p>
      <HighchartsReact highcharts={Highcharts} options={socOptions} />

      <p style={{ color: "#7e7e82", fontSize: 12, margin: "20px 0 8px" }}>
        PLD horário (R$/MWh) — contexto de preço
      </p>
      <HighchartsReact highcharts={Highcharts} options={pldOptions} />

      <p style={{ color: "#7e7e82", fontSize: 12, margin: "20px 0 8px" }}>
        Resultado de MCP por hora contra o PLD — verde é crédito, vermelho é
        débito na liquidação
      </p>
      <HighchartsReact highcharts={Highcharts} options={mcpOptions} />
    </div>
  );
};
